import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, X, Star, DollarSign, Clock, Shield, MessageSquare, Calendar, ChevronDown, RotateCcw } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * Advanced Search Filters Component
 * Extensible design - add new filters by:
 * 1. Adding to the backend /businesses/filters response
 * 2. Adding a new filter UI component in the filterComponents object
 * 3. Adding the filter param to handleApply
 */
export default function AdvancedFilters({ onApply, activeFilters = {}, className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filters, setFilters] = useState(activeFilters);
  const [loading, setLoading] = useState(false);

  // Fetch available filter options on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await fetch(`${API}/businesses/filters`);
        if (response.ok) {
          const data = await response.json();
          setFilterOptions(data.filters);
        }
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };
    fetchFilters();
  }, []);

  // Sync with external active filters
  useEffect(() => {
    setFilters(activeFilters);
  }, [activeFilters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApply = () => {
    onApply(filters);
    setIsOpen(false);
  };

  const handleReset = () => {
    setFilters({});
    onApply({});
    setIsOpen(false);
  };

  const activeCount = Object.values(filters).filter(v => v !== null && v !== undefined && v !== false).length;

  // Filter UI Components - extend this object for new filter types
  const filterComponents = {
    // Price Range Filter
    price_range: (config) => (
      <div className="space-y-2">
        <label className="text-sm font-medium text-zinc-300">{config.label}</label>
        <div className="flex gap-2">
          {config.options?.map((option) => (
            <button
              key={option.value}
              onClick={() => handleFilterChange('price_range', filters.price_range === option.value ? null : option.value)}
              className={`px-4 py-2 rounded-lg border transition-all ${
                filters.price_range === option.value
                  ? 'bg-white text-black border-white'
                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
              }`}
              data-testid={`filter-price-${option.value}`}
            >
              <span className="font-mono">{option.label}</span>
              {option.count > 0 && (
                <span className="ml-1 text-xs opacity-60">({option.count})</span>
              )}
            </button>
          ))}
        </div>
      </div>
    ),

    // Rating Filter
    min_rating: (config) => (
      <div className="space-y-2">
        <label className="text-sm font-medium text-zinc-300">{config.label}</label>
        <div className="flex flex-wrap gap-2">
          {config.options?.map((option) => (
            <button
              key={option.value || 'any'}
              onClick={() => handleFilterChange('min_rating', filters.min_rating === option.value ? null : option.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                filters.min_rating === option.value
                  ? 'bg-white text-black border-white'
                  : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
              }`}
              data-testid={`filter-rating-${option.value || 'any'}`}
            >
              {option.value && <Star className="w-4 h-4 fill-current" />}
              <span>{option.label}</span>
              {option.count > 0 && (
                <span className="text-xs opacity-60">({option.count})</span>
              )}
            </button>
          ))}
        </div>
      </div>
    ),

    // Toggle Filters
    open_now: (config) => (
      <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-colors">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-green-400" />
          <div>
            <span className="text-sm font-medium text-zinc-200">{config.label}</span>
            {config.description && (
              <p className="text-xs text-zinc-500">{config.description}</p>
            )}
          </div>
        </div>
        <button
          onClick={() => handleFilterChange('open_now', !filters.open_now)}
          className={`w-12 h-6 rounded-full transition-colors relative ${
            filters.open_now ? 'bg-green-500' : 'bg-zinc-700'
          }`}
          data-testid="filter-open-now"
        >
          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
            filters.open_now ? 'translate-x-7' : 'translate-x-1'
          }`} />
        </button>
      </div>
    ),

    verified_only: (config) => (
      <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-colors">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-blue-400" />
          <div>
            <span className="text-sm font-medium text-zinc-200">{config.label}</span>
            {config.count > 0 && (
              <p className="text-xs text-zinc-500">{config.count} businesses</p>
            )}
          </div>
        </div>
        <button
          onClick={() => handleFilterChange('verified_only', !filters.verified_only)}
          className={`w-12 h-6 rounded-full transition-colors relative ${
            filters.verified_only ? 'bg-blue-500' : 'bg-zinc-700'
          }`}
          data-testid="filter-verified-only"
        >
          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
            filters.verified_only ? 'translate-x-7' : 'translate-x-1'
          }`} />
        </button>
      </div>
    ),

    has_reviews: (config) => (
      <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-colors">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-5 h-5 text-purple-400" />
          <div>
            <span className="text-sm font-medium text-zinc-200">{config.label}</span>
            {config.count > 0 && (
              <p className="text-xs text-zinc-500">{config.count} businesses</p>
            )}
          </div>
        </div>
        <button
          onClick={() => handleFilterChange('has_reviews', !filters.has_reviews)}
          className={`w-12 h-6 rounded-full transition-colors relative ${
            filters.has_reviews ? 'bg-purple-500' : 'bg-zinc-700'
          }`}
          data-testid="filter-has-reviews"
        >
          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
            filters.has_reviews ? 'translate-x-7' : 'translate-x-1'
          }`} />
        </button>
      </div>
    ),

    // Range Filter (Year Established)
    year_established: (config) => (
      <div className="space-y-3">
        <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          {config.label}
        </label>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-zinc-500 block mb-1">From</label>
            <input
              type="number"
              placeholder={config.min || "1900"}
              value={filters.year_established_min || ''}
              onChange={(e) => handleFilterChange('year_established_min', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              min={config.min || 1900}
              max={new Date().getFullYear()}
              data-testid="filter-year-min"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-zinc-500 block mb-1">To</label>
            <input
              type="number"
              placeholder={config.max || new Date().getFullYear()}
              value={filters.year_established_max || ''}
              onChange={(e) => handleFilterChange('year_established_max', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              min={config.min || 1900}
              max={new Date().getFullYear()}
              data-testid="filter-year-max"
            />
          </div>
        </div>
      </div>
    )
  };

  return (
    <div className={className}>
      {/* Filter Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
          activeCount > 0
            ? 'bg-white text-black border-white'
            : 'border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white'
        }`}
        data-testid="open-filters-btn"
      >
        <SlidersHorizontal className="w-4 h-4" />
        <span className="text-sm font-medium">Filters</span>
        {activeCount > 0 && (
          <span className="w-5 h-5 rounded-full bg-black text-white text-xs flex items-center justify-center">
            {activeCount}
          </span>
        )}
      </button>

      {/* Filter Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0a0a0a] border-l border-zinc-800 z-50 overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-[#0a0a0a] border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <SlidersHorizontal className="w-5 h-5 text-zinc-400" />
                  <h2 className="text-lg font-semibold text-white">Filters</h2>
                  {activeCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-white text-black text-xs font-medium">
                      {activeCount} active
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full hover:bg-zinc-800 transition-colors"
                  data-testid="close-filters-btn"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              {/* Filter Content */}
              <div className="p-6 space-y-6">
                {filterOptions ? (
                  Object.entries(filterOptions).map(([key, config]) => (
                    <div key={key}>
                      {filterComponents[key] ? filterComponents[key](config) : null}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    Loading filters...
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="sticky bottom-0 bg-[#0a0a0a] border-t border-zinc-800 px-6 py-4 flex gap-3">
                <button
                  onClick={handleReset}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
                  data-testid="reset-filters-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
                <button
                  onClick={handleApply}
                  className="flex-1 px-4 py-3 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
                  data-testid="apply-filters-btn"
                >
                  Apply Filters
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
